/**
 * useAvatarVoiceChat — realtime voice with an artist via the SAME ElevenLabs
 * Conversational AI agent the web app uses (HypeTunes_Web_2026
 * src/hooks/useAvatarVoiceSession.js). Faithful port — same endpoints, same
 * WebSocket protocol, same thread, same create_song tool — plus ONE addition
 * for the 3D world: the agent's streamed audio drives the character (talkBridge
 * amplitude + worldStore.residentTalking) so the resident animates while it
 * speaks. NO Whisper, NO new brain.
 *
 * Reliability fixes over a naive port:
 *  - PRE-CONNECT on engage (mount): the WebSocket opens as soon as the chat
 *    opens, so the first words aren't eaten by ~1s of connect latency.
 *  - wireMic() runs whenever BOTH the socket is open AND the mic is acquired,
 *    not only in ws.onopen — so press-then-connect and connect-then-press both work.
 *  - Mic + audio contexts are acquired/unlocked inside the button GESTURE
 *    (iOS blocks getUserMedia / suspends AudioContexts outside a user gesture).
 *  - Diagnostics: logs mic settings + outgoing RMS so "it can't hear me" is
 *    debuggable from the console.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_BASE, AUTH_TOKEN } from '../config';
import { useWorldStore } from '../state/worldStore';
import { talkBridge } from '../state/talkBridge';
import { audioEngine } from '../services/audioEngine';

const IDLE_TIMEOUT_MS = 90000; // auto-disconnect after silence so nothing bills forever

// ── THE SESSION ENDS IN CHARACTER, NOT MID-SENTENCE ─────────────────────────
//
// Khari, 2026-08-06: "if the user is speaking, the last message goes unheard in
// a quietly disconnected session. If we know the session is coming to a close,
// the avatar should say so.. 'yo, I think we're out of time, but I'm here if
// you need to speak more.'" And the rules: user speaking → farewell over them;
// avatar speaking → finish the statement, THEN farewell.
//
// The clock that matters is not ours to see: every agent is created with a
// 300-second provider-side cap (ElevenLabsAgentService, conversation.
// max_duration_seconds) and when IT lands the socket just closes — no event, no
// warning, indistinguishable from a crash. So OUR clock has to run shorter than
// theirs, with enough room left to say goodbye in.
//
// The farewell is asked for, not scripted: a user_message on the open socket
// tells the agent time is up, and it answers in its own voice and words — every
// artist says goodbye differently, because they actually do. The agent's own
// turn-taking gives Khari both rules for free: an instruction arriving while it
// speaks queues behind the current sentence rather than cutting it.
const MEMBER_CAP_S = 300;        // mirror of the agent config's own number
const FAREWELL_HEADROOM_S = 25;  // ask this early, so there is room to answer
const HARD_STOP_MARGIN_S = 3;    // and always end INSIDE the provider's kill
const FAREWELL_PROMPT = '(The session clock just ran out. Say a short, warm goodbye in your own voice — you have to go, but they can come back and talk to you any time. One or two sentences, then stop.)';
const TIME_UP_NOTICE = '(Time limit reached. Session closed.)';
// Mic energy (RMS) that counts as "the user is actually talking" while a reply
// is playing. Tuned high on purpose: a missed interruption costs a second of
// patience, a false one cuts the agent off mid-sentence for nothing.
const BARGE_IN_RMS = 0.055;

// `guest` + `email` route through the email-gated, no-auth guest voice endpoints
// (email-capped, ~2 min/session) instead of the logged-in voice-session.
export default function useAvatarVoiceChat({ avatarId, token = AUTH_TOKEN, guest = false, email = '' }) {
  const [active, setActive] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [micOpen, setMicOpen] = useState(false);
  const [awaitingReply, setAwaitingReply] = useState(false);
  const [caption, setCaption] = useState('');
  const [error, setError] = useState('');
  const [requestingMic, setRequestingMic] = useState(false); // browser permission prompt is up
  const [micDenied, setMicDenied] = useState(false);         // mic blocked → offer text fallback

  const wsRef = useRef(null);
  const streamRef = useRef(null);
  const playCtxRef = useRef(null);
  const micCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const procRef = useRef(null);
  const rafRef = useRef(null);
  const idleRef = useRef(null);
  const capRef = useRef(null);       // hard stop — always inside the provider's own kill
  const farewellRef = useRef(null);  // the earlier timer that asks for the goodbye
  const drainEndRef = useRef(null);  // grace timer between audio chunks of the goodbye
  const closingRef = useRef(null);   // null | 'asked' | 'done'
  const threadIdRef = useRef(null);
  const agentIdRef = useRef(null);
  const agentSpeakingRef = useRef(false);
  const micOpenRef = useRef(false);
  const interruptRef = useRef(null);   // stops the reply mid-chunk (barge-in)
  const avatarRef = useRef(avatarId);
  avatarRef.current = avatarId;

  const setSpeakingBoth = useCallback((v) => {
    setSpeaking(v);
    useWorldStore.getState().setResidentTalking(v);
    talkBridge.talking = v;
    if (!v) talkBridge.amplitude = 0;
    if (v) setAwaitingReply(false);
  }, []);

  const endRef = useRef(() => {});
  const end = useCallback(() => {
    if (idleRef.current) { clearTimeout(idleRef.current); idleRef.current = null; }
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (procRef.current) { try { procRef.current.disconnect(); } catch { /* noop */ } procRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    if (wsRef.current) { try { wsRef.current.close(); } catch { /* noop */ } wsRef.current = null; }
    if (micCtxRef.current) { micCtxRef.current.close().catch(() => {}); micCtxRef.current = null; }
    if (playCtxRef.current) { playCtxRef.current.close().catch(() => {}); playCtxRef.current = null; }
    analyserRef.current = null;
    setActive(false); setConnecting(false); setAwaitingReply(false); setMicOpen(false);
    micOpenRef.current = false;
    setSpeakingBoth(false);
    agentSpeakingRef.current = false;
    interruptRef.current = null;
    audioEngine.duck(false);
    if (capRef.current) { clearTimeout(capRef.current); capRef.current = null; }
    if (farewellRef.current) { clearTimeout(farewellRef.current); farewellRef.current = null; }
    if (drainEndRef.current) { clearTimeout(drainEndRef.current); drainEndRef.current = null; }
    closingRef.current = null;
    if (agentIdRef.current) {
      fetch(`${API_BASE}${guest ? '/api/chat/guest/voice-session/end' : '/api/chat/voice-session/end'}`, {
        method: 'POST',
        headers: guest ? { 'Content-Type': 'application/json' } : { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId: agentIdRef.current }),
      }).catch((err) => console.warn('[VoiceSession] Agent cleanup failed:', err));
      agentIdRef.current = null;
    }
    threadIdRef.current = null;
  }, [token, guest, setSpeakingBoth]);
  endRef.current = end;

  const resetIdle = useCallback(() => {
    if (idleRef.current) clearTimeout(idleRef.current);
    idleRef.current = setTimeout(() => { endRef.current(); }, IDLE_TIMEOUT_MS);
  }, []);

  const saveTurn = useCallback((role, content) => {
    if (!threadIdRef.current) return;
    fetch(`${API_BASE}/api/chat/voice-session/save`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ threadId: threadIdRef.current, messages: [{ role, content }] }),
    }).catch((err) => console.warn('[VoiceSession] Save failed:', err));
  }, [token]);

  // Build the mic → WS pipe. Safe to call repeatedly / from either side; only
  // wires once BOTH the socket is open and the mic is acquired.
  const wireMic = useCallback(() => {
    if (procRef.current) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !streamRef.current || !micCtxRef.current) return;
    const micContext = micCtxRef.current;
    const source = micContext.createMediaStreamSource(streamRef.current);
    const processor = micContext.createScriptProcessor(4096, 1, 1);
    processor.onaudioprocess = (e) => {
      if (ws.readyState !== WebSocket.OPEN) return;
      const input = e.inputBuffer.getChannelData(0);

      // BARGE-IN. The mic used to go deaf entirely while the agent spoke, so
      // the only way to stop it was to wait. Now speech gets through — but only
      // speech: everything the mic hears while a reply is playing is echo and
      // room tone until it clearly isn't, and forwarding that would have the
      // agent interrupting itself.
      //
      // Browser AEC (echoCancellation, on in prewarm) does the real work; this
      // energy gate is the backstop for what leaks past it. Deliberately high —
      // a missed interruption costs a second of patience, a false one cuts the
      // agent off mid-sentence for nothing.
      if (agentSpeakingRef.current) {
        let sum = 0;
        for (let i = 0; i < input.length; i++) sum += input[i] * input[i];
        if (Math.sqrt(sum / input.length) < BARGE_IN_RMS) return;
        // Kill the reply LOCALLY and now. The server will also stop sending,
        // but audio already queued here would otherwise keep playing over the
        // interruption — which is exactly what makes an assistant feel deaf.
        interruptRef.current?.();
      }

      const int16 = new Int16Array(input.length);
      // Floor closed → send silence (not nothing) so turn detection still fires.
      if (micOpenRef.current) {
        for (let i = 0; i < input.length; i++) int16[i] = Math.max(-32768, Math.min(32767, Math.round(input[i] * 32767)));
      }
      const bytes = new Uint8Array(int16.buffer);
      let binary = '';
      for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
      ws.send(JSON.stringify({ user_audio_chunk: btoa(binary) }));
    };
    source.connect(processor);
    processor.connect(micContext.destination);
    procRef.current = processor;
  }, []);

  // Acquire mic + unlock BOTH audio contexts — MUST run inside a user gesture (iOS).
  const prewarm = useCallback(async () => {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!playCtxRef.current) playCtxRef.current = new AC({ sampleRate: 16000 });
    await playCtxRef.current.resume().catch(() => {});
    if (!streamRef.current) {
      streamRef.current = await navigator.mediaDevices.getUserMedia({
        audio: { sampleRate: 16000, channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      micCtxRef.current = new AC({ sampleRate: 16000 });
    }
    await micCtxRef.current.resume().catch(() => {});
    wireMic();
  }, [wireMic]);

  const start = useCallback(async () => {
    if (active || connecting || wsRef.current) return;
    // Caption cleared too: the time-up notice is written THROUGH the caption and
    // deliberately outlives its session — a new session must not open under the
    // old one's closing line.
    setConnecting(true); setError(''); setCaption('');
    try {
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!playCtxRef.current) playCtxRef.current = new AC({ sampleRate: 16000 });
      await playCtxRef.current.resume().catch(() => {});
      const res = await fetch(`${API_BASE}${guest ? '/api/chat/guest/voice-session' : '/api/chat/voice-session'}`, {
        method: 'POST',
        headers: guest ? { 'Content-Type': 'application/json' } : { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(guest ? { email, avatarId: avatarRef.current } : { avatarId: avatarRef.current }),
      });
      const data = await res.json();
      if (res.status === 403 && data.requiresUpgrade) { setError(data.error || "That's your free voice time — sign up to keep talking."); setConnecting(false); return; }
      if (!data.success || !data.signedUrl) throw new Error(data.error || 'Failed to start voice session');
      threadIdRef.current = data.threadId || null;
      agentIdRef.current = data.agentId;

      // The closing beat, on whichever clock binds. Guests get maxSeconds from
      // the server (~2 min); the member endpoint historically sent nothing, so
      // the fallback is the agent's own 300s — the cap that WAS ending member
      // calls, silently, by closing the socket. It never stopped applying; it
      // just finally gets a goodbye in front of it.
      const capSeconds = Number(data.maxSeconds) || MEMBER_CAP_S;
      closingRef.current = null;

      const endForTime = () => {
        if (closingRef.current === 'done') return;
        closingRef.current = 'done';
        // The caption outlives the session on every dock, so the farewell is
        // legible as a rule rather than a brush-off — the other half of the
        // spec, and neither half works alone.
        setCaption(TIME_UP_NOTICE);
        endRef.current();
      };
      const sayGoodbye = () => {
        if (closingRef.current) return;
        closingRef.current = 'asked';
        const sock = wsRef.current;
        if (!sock || sock.readyState !== WebSocket.OPEN) { endForTime(); return; }
        try {
          sock.send(JSON.stringify({ type: 'user_message', text: FAREWELL_PROMPT }));
        } catch { endForTime(); }
      };
      if (farewellRef.current) clearTimeout(farewellRef.current);
      farewellRef.current = setTimeout(sayGoodbye, Math.max(5, capSeconds - FAREWELL_HEADROOM_S) * 1000);
      // The backstop the farewell cannot escape: a mute agent, a dead socket, a
      // goodbye that will not stop talking — whatever happens, WE end it, inside
      // the provider's own kill, so the last thing seen is our notice and never
      // an unexplained drop.
      if (capRef.current) clearTimeout(capRef.current);
      capRef.current = setTimeout(endForTime, Math.max(8, capSeconds - HARD_STOP_MARGIN_S) * 1000);

      const audioContext = playCtxRef.current;
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.connect(audioContext.destination);
      analyserRef.current = analyser;
      const ampData = new Uint8Array(analyser.frequencyBinCount);
      const ampTick = () => {
        const a = analyserRef.current;
        if (!a) return;
        a.getByteTimeDomainData(ampData);
        let sum = 0;
        for (let i = 0; i < ampData.length; i++) { const v = (ampData[i] - 128) / 128; sum += v * v; }
        talkBridge.amplitude = agentSpeakingRef.current ? Math.sqrt(sum / ampData.length) : talkBridge.amplitude * 0.8;
        rafRef.current = requestAnimationFrame(ampTick);
      };
      ampTick();

      const ws = new WebSocket(data.signedUrl);
      wsRef.current = ws;

      const queue = [];
      let playing = false;
      let curSrc = null;
      const playNext = () => {
        if (queue.length === 0) {
          playing = false; curSrc = null; agentSpeakingRef.current = false; setSpeakingBoth(false);
          // The goodbye has (probably) finished. Audio arrives in chunks, so an
          // empty queue can also be a gap mid-sentence — the grace window lets a
          // late chunk cancel the ending (see the audio branch below). Barging
          // in over the farewell lands here too, via the interrupt clearing the
          // queue: talking over the goodbye ends the call, which is the rule.
          if (closingRef.current === 'asked') {
            if (drainEndRef.current) clearTimeout(drainEndRef.current);
            drainEndRef.current = setTimeout(endForTime, 1200);
          }
          return;
        }
        playing = true; agentSpeakingRef.current = true; setSpeakingBoth(true);
        const pcm = queue.shift();
        const float32 = new Float32Array(pcm.length);
        for (let i = 0; i < pcm.length; i++) float32[i] = pcm[i] / 32768;
        const buffer = audioContext.createBuffer(1, float32.length, 16000);
        buffer.getChannelData(0).set(float32);
        const src = audioContext.createBufferSource();
        src.buffer = buffer;
        src.connect(analyser);
        src.onended = playNext;
        curSrc = src;
        src.start();
      };

      // Stop talking, immediately. Audio arrives in chunks and buffers ahead, so
      // dropping the queue is not enough on its own — the chunk already playing
      // has to be cut too, or the reply carries on for a beat after you have
      // started speaking. Clearing onended first stops it pulling the next one.
      interruptRef.current = () => {
        queue.length = 0;
        if (curSrc) {
          try { curSrc.onended = null; curSrc.stop(); } catch { /* already ended */ }
          curSrc = null;
        }
        playing = false;
        agentSpeakingRef.current = false;
        setSpeakingBoth(false);
      };

      ws.onopen = () => {
        setActive(true); setConnecting(false); resetIdle();
        // STANDARD DUCK, NOT SILENCE (Khari, 2026-08-04: "duck the music when I
        // start a voice conversation with any avatar" … "not muted.. same level
        // of standard ducking"). The bed stays audible under the conversation —
        // the city keeps playing while you talk to somebody in it, which is the
        // whole feeling. `full` is reserved for video calls, where the music is
        // genuinely not part of the moment.
        audioEngine.duck(true);
        wireMic(); // wires if the mic is already acquired; else prewarm() will
      };

      ws.onmessage = (event) => {
        resetIdle();
        try {
          const msg = JSON.parse(event.data);
          const audioB64 = msg.audio || msg.audio_event?.audio_base_64;
          if (audioB64) {
            // Still talking — a drained queue was a chunk gap, not the end of
            // the goodbye. Stand the ending down until it drains again.
            if (drainEndRef.current) { clearTimeout(drainEndRef.current); drainEndRef.current = null; }
            const bin = atob(audioB64);
            const bytes = new Uint8Array(bin.length);
            for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
            queue.push(new Int16Array(bytes.buffer));
            if (!playing) playNext();
          }
          // The agent tells us when it has been cut off; honour it locally so
          // buffered audio does not outlive the interruption.
          if (msg.type === 'interruption' || msg.interruption_event) interruptRef.current?.();
          const tEvent = msg.user_transcription_event || msg.user_transcript_event;
          if (msg.type === 'user_transcript' && tEvent?.user_transcript) saveTurn('user', tEvent.user_transcript);
          if (msg.type === 'agent_response' && msg.agent_response_event?.agent_response) {
            const text = msg.agent_response_event.agent_response;
            setCaption(text);
            saveTurn('assistant', text);
          }
          if (msg.type === 'client_tool_call' && msg.client_tool_call) {
            const { tool_call_id, tool_name, parameters } = msg.client_tool_call;
            if (tool_name === 'create_song' && threadIdRef.current) {
              fetch(`${API_BASE}/api/chat/voice-session/create-song`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  threadId: threadIdRef.current, avatarId: avatarRef.current,
                  title: parameters.title, concept: parameters.concept, genre: parameters.genre,
                  mood: parameters.mood, style: parameters.style, voice_type: parameters.voice_type,
                  use_user_voice: parameters.use_user_voice || false,
                }),
              })
                .then((r) => r.json())
                .then((result) => {
                  const toolResult = result.success ? result.message : `Song creation failed: ${result.error}`;
                  if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: 'client_tool_result', tool_call_id, result: toolResult }));
                })
                .catch((err) => {
                  console.error('[VoiceSession] create_song failed:', err);
                  if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send(JSON.stringify({ type: 'client_tool_result', tool_call_id, result: 'Song creation failed due to a technical error. Please try again.' }));
                });
            }
          }
        } catch { /* binary/unparseable — ignore */ }
      };

      ws.onerror = (err) => { console.error('[VoiceSession] WS error:', err); setError('Voice session error'); endRef.current(); };
      ws.onclose = () => { setActive(false); setSpeakingBoth(false); };
    } catch (err) {
      console.error('[VoiceSession] Failed to start:', err);
      setError(err.message || 'Failed to start voice session');
      setConnecting(false);
    }
  }, [active, connecting, token, guest, email, resetIdle, saveTurn, setSpeakingBoth, wireMic]);
  const startRef = useRef(start);
  startRef.current = start;

  // TALK IS A TOGGLE, NOT A HOLD.
  //
  // Hold-to-talk asked people to learn a gesture before they could say anything,
  // and it reads as broken the first time you tap and nothing happens. It also
  // fights the medium: this is a STREAMING agent that handles its own
  // turn-taking, so gating the mic on a held button was throwing away the one
  // thing it is good at.
  //
  // Press once: the music ducks and the conversation is open until you end it.
  // The mic stays live throughout — interrupting is just talking, the way it is
  // on a phone call.
  const toggleTalk = useCallback(async () => {
    // Second press = hang up. Reads as one control with two states rather than
    // a start button and a separate, easily-missed end button.
    if (active || connecting || micOpenRef.current) { endRef.current(); return; }

    micOpenRef.current = true; setMicOpen(true); setError(''); setRequestingMic(true);
    try {
      // Mic + audio unlock must happen INSIDE the gesture or the browser
      // refuses both — that constraint is why this was a hold in the first
      // place, and a click satisfies it just as well.
      await prewarm();
    } catch (err) {
      console.error('[VoiceSession] mic prewarm failed:', err);
      micOpenRef.current = false; setMicOpen(false); setRequestingMic(false); setMicDenied(true);
      setError('Mic access is blocked');
      return;
    }
    setRequestingMic(false);
    // Connect ONLY now, on a deliberate press, so entering the world — or just
    // seeing the mic — never creates or bills a session. start() ducks the
    // music (duck(true)); end() restores it.
    if (!active && !connecting && !wsRef.current) startRef.current();
    else wireMic();
  }, [active, connecting, prewarm, wireMic]);

  // Clean up on unmount only. NEVER auto-connect on mount — a session is created
  // strictly on the first deliberate press (above).
  useEffect(() => () => { endRef.current(); }, []);

  const phase = useMemo(() => {
    if (error) return 'error';
    if (connecting && !active) return 'connecting';
    if (speaking) return 'speaking';
    if (micOpen && active) return 'listening';
    if (awaitingReply) return 'thinking';
    return 'idle';
  }, [error, connecting, speaking, micOpen, active, awaitingReply]);

  return { phase, caption, error, active, toggleTalk, end, requestingMic, micDenied };
}
